import { ColumnReader } from './metabaseTable';

/**
 * "Why This Result" — the governed explanation Metabase returns ALONGSIDE a metric's own reading
 * when the request carries `with_wtr=true`, as a SEPARATE row: `result_role: 'wtr'` rather than
 * `'current'`. One row is one reason; a metric can answer with several, ranked.
 *
 * VERIFIED LIVE on UAT `generic` 2026-09-14: `with_wtr=true` is accepted and the schema below is
 * real (checked on O-COM-009, `?with_wtr=true`, full column list returned). No metric in a
 * ~20-identifier sweep of the Layer 1 registry returned an actual `result_role: 'wtr'` row in that
 * sweep — the backend team states 622 of 3,169 WTR runtime keys are implemented so far, and this
 * tenant's currently-computed metrics were not among them at the time of checking. Also VERIFIED:
 * `with_wtr=true` does not degrade gracefully on a metric whose WTR keys are not yet implemented —
 * O-COM-005/006/008/010/012/016/021/023 each return `50201` (provider error) WITH the param and
 * `200` without it, on the identical date range. This contradicts the "normal payload still works"
 * claim for those metrics. The Data team's Phase 1B handoff makes `with_wtr=true` the contract
 * (167 WTR-enabled cards), so `api/customerInsights.ts` now sends it on every metric and retries
 * once without it on a 502.
 */
export const WTR_RESULT_ROLE = 'wtr';

/** Whether this raw result row is a reason row rather than a reading. */
export const isWtrRow = (read: ColumnReader, row: unknown[]): boolean =>
  read.text(row, 'result_role') === WTR_RESULT_ROLE;

/**
 * The rows a READING parse may look at, in their incoming order.
 *
 * `rowsFromEnvelope` sorts wtr rows to the end so they can never become `results[0]`/`results[1]`,
 * which is enough for the scalar path. Every other parse on this endpoint reads the RAW rows
 * instead -- the distribution segments, the reversal shapes, the readiness rows -- and each treats
 * a row it does not recognise as a withheld reading, so a reason row reaching one of them draws an
 * empty segment beside the real data. Filtered by `result_role` and not by position: the contract
 * does not state where the reason rows land in the result set. PLA-8828.
 */
export const readingRowsOnly = (read: ColumnReader, rows: unknown[][]): unknown[][] =>
  rows.filter(row => !isWtrRow(read, row));

/** The four name/value pairs a template's placeholders substitute from. */
export interface WtrParam {
  name: string;
  value: string;
}

/** One governed reason, as the wire stated it — verbatim, never re-derived (same rule as confidence, G32). */
export interface WtrReason {
  reasonCode: string;
  reasonType: string | null;
  ruleId: string | null;
  ruleFamily: string | null;
  /** Governed ordering between reasons, before dedup/selection. */
  priority: number | null;
  /** Final ordering after dedup/selection — what a reader should sort a metric's reasons by. */
  selectionRank: number | null;
  aggregationGroup: string | null;
  scope: string | null;
  unitId: string | null;
  /** The governed sentence template, with `{paramName}` placeholders. `renderWtrReason` fills them. */
  templateText: string | null;
  params: WtrParam[];
  currentValue: number | null;
  previousValue: number | null;
  delta: number | null;
  relativeChange: number | null;
  movementState: string | null;
  thresholdUsed: string | null;
  comparisonBasis: string | null;
}

const PARAM_POSITIONS = [1, 2, 3, 4] as const;

// `{driver_change}`, `{comparison_period}` -- the template vocabulary the data team stated, which is
// i18next's `{{var}}` with one brace. Matched on anything between the braces rather than on a name
// pattern, so an unresolved placeholder is recognised however it is spelled.
const ANY_PLACEHOLDER = /\{[^{}]+\}/;

const paramAt = (
  read: ColumnReader,
  row: unknown[],
  position: (typeof PARAM_POSITIONS)[number]
): WtrParam | null => {
  const name = read.text(row, `wtr_param_${position}_name`);
  const value = read.text(row, `wtr_param_${position}_value`);

  return name !== null && value !== null ? { name, value } : null;
};

/**
 * A `result_role: 'wtr'` row into one `WtrReason`, or `undefined` for any other row — including one
 * that carries no `wtr_reason_code` at all, which is the wire's way of stating "no reason fired",
 * not a reason with an empty code.
 */
export const wtrFromRow = (read: ColumnReader, row: unknown[]): WtrReason | undefined => {
  if (read.text(row, 'result_role') !== WTR_RESULT_ROLE) {
    return undefined;
  }

  const reasonCode = read.text(row, 'wtr_reason_code');

  if (reasonCode === null) {
    return undefined;
  }

  return {
    reasonCode,
    reasonType: read.text(row, 'wtr_reason_type'),
    ruleId: read.text(row, 'wtr_rule_id'),
    ruleFamily: read.text(row, 'wtr_rule_family'),
    priority: read.number(row, 'wtr_priority'),
    selectionRank: read.number(row, 'wtr_selection_rank'),
    aggregationGroup: read.text(row, 'wtr_aggregation_group'),
    scope: read.text(row, 'wtr_scope'),
    unitId: read.text(row, 'wtr_unit_id'),
    templateText: read.text(row, 'wtr_template_text'),
    params: PARAM_POSITIONS.map(position => paramAt(read, row, position)).filter(
      (param): param is WtrParam => param !== null
    ),
    currentValue: read.number(row, 'wtr_current_value'),
    previousValue: read.number(row, 'wtr_previous_value'),
    delta: read.number(row, 'wtr_delta'),
    relativeChange: read.number(row, 'wtr_relative_change'),
    movementState: read.text(row, 'wtr_movement_state'),
    thresholdUsed: read.text(row, 'wtr_threshold_used'),
    comparisonBasis: read.text(row, 'wtr_comparison_basis')
  };
};

/**
 * `wtr_template_text` with every `{paramName}` placeholder substituted from `params`, or `null`.
 *
 * `null` when there is no template to render, and `null` again when substitution leaves a
 * placeholder behind. A sentence still showing `{subject_name}` is worse than no sentence -- the
 * same stance the confidence catalogue takes in `i18n/locales/en/translations.ts`
 * § confidenceGovernance -- so nothing is drawn rather than a half-rendered governed claim.
 *
 * Values are substituted VERBATIM. No rounding, no date formatting, no pluralisation: the sentence
 * and its figures are the backend's governed copy, and this side is not entitled to restate either.
 */
export const renderWtrReason = (reason: WtrReason): string | null => {
  const template = typeof reason?.templateText === 'string' ? reason.templateText.trim() : '';

  if (template === '') {
    return null;
  }

  const rendered = reason.params.reduce(
    (text, param) => text.split(`{${param.name}}`).join(param.value),
    template
  );

  return ANY_PLACEHOLDER.test(rendered) ? null : rendered;
};

/**
 * One reason as a CARD draws it: the backend's sentence, plus the tile it was read for.
 *
 * Rendered on demand and never stored: `IntelligenceFixture` carries the reasons as the wire stated
 * them (`whyThisResultReasons`, and per tile on `CardMetric.reasons`), and a second copy holding the
 * rendered text would be two answers to one question the moment a template changed.
 */
export interface CardReason {
  code: string;
  type: string | null;
  text: string;
  /** The tile whose metric stated it, where the card can attribute it. */
  metricId?: string;
  /** That tile's label, where the card has one. */
  metricName?: string;
}

// Nulls last, and arrival order breaks the remaining ties. A reason the backend chose not to rank
// must not sort ahead of the one it ranked first.
const RANK_LAST = Number.POSITIVE_INFINITY;

interface Attribution {
  reason: WtrReason;
  metricId?: string;
  metricName?: string;
}

/**
 * Every reason a card can draw, rendered to sentences and merged into ONE ranked list.
 *
 * Attribution comes from the tiles, which is why this reads the fixture rather than the card-level
 * array alone: `whyThisResultReasons` is the same set flattened, and a reason it carries that no
 * tile claims is still listed, unattributed, rather than dropped. A reason that did not render is
 * dropped -- see `renderWtrReason`. PLA-8828.
 */
export const renderedCardReasons = (
  fixture:
    | {
        metrics?: { key?: string; label?: string; reasons?: WtrReason[] }[];
        whyThisResultReasons?: WtrReason[];
      }
    | undefined
    | null
): CardReason[] => {
  // First tile wins. Two tiles can read ONE identifier -- a `ratio` operand and the tile it feeds --
  // and both are handed the same reason objects, so without the claim the card lists one twice.
  const claimed = new Set<WtrReason>();
  const attributed = (fixture?.metrics ?? []).reduce<Attribution[]>((kept, metric) => {
    const fresh = (metric?.reasons ?? []).filter(reason => !claimed.has(reason));

    fresh.forEach(reason => claimed.add(reason));

    return [
      ...kept,
      ...fresh.map(reason => ({ reason, metricId: metric?.key, metricName: metric?.label }))
    ];
  }, []);
  const rest: Attribution[] = (fixture?.whyThisResultReasons ?? [])
    .filter(reason => !claimed.has(reason))
    .map(reason => ({ reason }));

  return [...attributed, ...rest]
    .map((entry, index) => ({ ...entry, index }))
    .sort(
      (left, right) =>
        (left.reason.selectionRank ?? RANK_LAST) - (right.reason.selectionRank ?? RANK_LAST) ||
        left.index - right.index
    )
    .reduce<CardReason[]>((kept, entry) => {
      const text = renderWtrReason(entry.reason);

      /* One sentence per code in a card's box, the highest-ranked copy kept, whichever metric sent
         it. Claiming above is by object identity, which two copies of one reason row defeat: in one
         response (O-INS-003's two RENEWAL_ENGAGEMENT_DECLINING rows) or across the card's metrics
         (O-COM-003's page response also carries O-COM-004's SOURCE_COVERAGE_COMPLETE). */
      const duplicate = kept.some(
        reason => reason.code === entry.reason.reasonCode && reason.text === text
      );

      return text === null || duplicate
        ? kept
        : [
            ...kept,
            {
              code: entry.reason.reasonCode,
              type: entry.reason.reasonType,
              text,
              ...(entry.metricId ? { metricId: entry.metricId } : {}),
              ...(entry.metricName ? { metricName: entry.metricName } : {})
            }
          ];
    }, []);
};
